import * as Yup from 'yup';
import { MESSAGE_PHONE_INVALID, MESSAGE_PHONE_NOT_RUSSIAN, MESSAGE_REQUIRED } from '@/utils/validation-utils';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import type { PhoneNumber } from 'libphonenumber-js/max';

/**
 * The rule for every phone field outside the profile — the letter and answer author forms,
 * and the card issuing form.
 * Any number libphonenumber considers valid is accepted, a foreign one included;
 * a value carrying no country code is resolved against the RU region.
 * Both rules read the same `max` metadata, so a number accepted here is never one
 * {@link russianPhoneFieldSchema} would call malformed; the two differ on the country alone.
 *
 * Deliberately looser than {@link russianPhoneFieldSchema}:
 * the Russia-only rule applies to the number bound to the account,
 * not to a contact number given while writing a letter.
 * Pair it with `PhoneNumberField`.
 */
export const phoneFieldSchema: Yup.StringSchema = Yup.string()
  .required(MESSAGE_REQUIRED)
  .test('phoneNumber', MESSAGE_PHONE_INVALID, (value: string | undefined) => {
    try {
      // The RU region resolves only a value carrying no country code, which this field never emits:
      // `PhoneNumberInput` always hands over E.164. The server parses with no default region,
      // so a bare national number would pass here and be refused there — unreachable through the UI,
      // and it fails closed if it ever is reached.
      return value ? isValidPhoneNumber(value, 'RU') : false;
    } catch {
      // A backstop rather than a path taken today:
      // `isValidPhoneNumber` answers false for the malformed input this field can hold.
      // Without it a throw would escape the Yup test as a validation crash —
      // the form refusing to submit with no message at all — instead of rejecting the number the user can correct.
      return false;
    }
  });

/**
 * Outcomes of the Russia-only rule, named after the codes the backend returns
 * so a rejection here can be traced to the server rule it mirrors.
 */
type PhoneVerdict = 'OK' | 'PHONE_NOT_RUSSIAN' | 'PHONE_INVALID';

/**
 * Mirrors the server rule in `PhoneUtils.validate`, which parses with libphonenumber
 * against the RU region and requires both a valid number and a Russian country.
 * `libphonenumber-js` is a port of the same Google metadata, so the two agree case for case.
 *
 * @param value The phone in any notation this field can hold — '+79001234567', '89001234567',
 *              '9001234567', or the masked '+7 (900) 123-45-67'.
 */
const resolvePhoneVerdict = (value: string): PhoneVerdict => {
  try {
    const parsed: PhoneNumber | undefined = parsePhoneNumberFromString(value, 'RU');
    if (!parsed) {
      return 'PHONE_INVALID';
    }
    if (!parsed.isValid()) {
      return 'PHONE_INVALID';
    }
    return parsed.country === 'RU' ? 'OK' : 'PHONE_NOT_RUSSIAN';
  } catch {
    // `parsePhoneNumberFromString` is the non-throwing variant, so this is a backstop rather than a path taken today.
    // Without it a throw would escape the Yup test as a validation crash —
    // the form would refuse to submit with no message — instead of rejecting the number the user can correct.
    return 'PHONE_INVALID';
  }
};

/**
 * The rule for the number bound to the account, used by the profile phone dialog and nowhere else.
 * Rejects a foreign number, Kazakhstan included, even though it shares the '+7' country code.
 * Pair it with `RussianPhoneNumberField`, which applies the same restriction to the input.
 */
export const russianPhoneFieldSchema: Yup.StringSchema = Yup.string()
  .required(MESSAGE_REQUIRED)
  .test('phoneNumber', MESSAGE_PHONE_INVALID, (value: string | undefined, context: Yup.TestContext) => {
    if (!value) {
      return false;
    }
    const verdict: PhoneVerdict = resolvePhoneVerdict(value);
    if (verdict === 'OK') {
      return true;
    }
    // Reporting which rule failed lets the user act: a foreign number needs replacing,
    // a malformed or unallocated one needs checking.
    return context.createError({
      message: verdict === 'PHONE_NOT_RUSSIAN' ? MESSAGE_PHONE_NOT_RUSSIAN : MESSAGE_PHONE_INVALID,
    });
  });
